import { useState, useMemo } from 'react';
import { Console } from './Console';
import './Output.css';

interface OutputProps {
  typescript: string;
  error: string | null;
}

type TabType = 'typescript' | 'console';

// TypeScript syntax highlighting
function highlightTypeScript(code: string): string {
  // Escape HTML first
  let highlighted = code
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Comments
  highlighted = highlighted.replace(
    /(\/\/[^\n]*)/g,
    '<span class="ts-comment">$1</span>'
  );

  // Strings
  highlighted = highlighted.replace(
    /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)/g,
    '<span class="ts-string">$1</span>'
  );

  // Numbers
  highlighted = highlighted.replace(
    /\b(\d+\.?\d*)\b/g,
    '<span class="ts-number">$1</span>'
  );

  // TypeScript keywords
  highlighted = highlighted.replace(
    /\b(const|let|var|function|class|interface|type|enum|return|if|else|while|for|try|catch|finally|throw|break|continue|new|this|async|await|import|export|from|extends|implements|as|public|private|protected|readonly|static|constructor)\b/g,
    '<span class="ts-keyword">$1</span>'
  );

  // TypeScript types
  highlighted = highlighted.replace(
    /\b(string|number|boolean|any|unknown|never|void|null|undefined|Array|Promise|Object)\b/g,
    '<span class="ts-type">$1</span>'
  );

  // Booleans and null
  highlighted = highlighted.replace(
    /\b(true|false|null|undefined)\b/g,
    '<span class="ts-literal">$1</span>'
  );

  // console.log etc
  highlighted = highlighted.replace(
    /\b(console)\.(log|warn|error)\b/g,
    '<span class="ts-builtin">$1</span>.<span class="ts-method">$2</span>'
  );

  return highlighted;
}

// Extract line number from error message
function parseErrorLine(error: string): number | null {
  // Look for patterns like "line 5" or "Ligne 5" or "[5:10]"
  const lineMatch = error.match(/(?:line|ligne)\s*(\d+)/i) ||
                    error.match(/\[(\d+):\d+\]/) ||
                    error.match(/at\s+.*:(\d+):\d+/);
  return lineMatch ? parseInt(lineMatch[1], 10) : null;
}

function Output({ typescript, error }: OutputProps) {
  const [activeTab, setActiveTab] = useState<TabType>('typescript');

  const lineCount = useMemo(() => {
    return typescript.split('\n').length;
  }, [typescript]);

  const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1);
  const errorLine = error ? parseErrorLine(error) : null;

  // Switch to console tab when running code
  const handleRunStart = () => {
    setActiveTab('console');
  };

  return (
    <div className="output">
      {/* Tabs Header */}
      <div className="output-tabs">
        <div className="output-tab-buttons">
          <button
            className={`output-tab ${activeTab === 'typescript' ? 'active' : ''}`}
            onClick={() => setActiveTab('typescript')}
          >
            TypeScript
          </button>
          <button
            className={`output-tab ${activeTab === 'console' ? 'active' : ''}`}
            onClick={() => setActiveTab('console')}
          >
            Console
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="output-error">
          <span className="error-icon">&#9888;</span>
          <span className="error-message">
            {errorLine ? `Ligne ${errorLine}: ` : ''}
            {error}
          </span>
        </div>
      )}

      {/* Tab Content */}
      <div className="output-content">
        {activeTab === 'typescript' && (
          <div className="output-code">
            {/* Line Numbers */}
            <div className="output-line-numbers">
              {lineNumbers.map(num => (
                <div key={num} className="line-number">
                  {num}
                </div>
              ))}
            </div>

            {/* TypeScript Code */}
            <pre
              className="output-highlight"
              dangerouslySetInnerHTML={{ __html: highlightTypeScript(typescript) || '<span class="output-placeholder">// Le code TypeScript apparaîtra ici...</span>' }}
            />
          </div>
        )}

        {activeTab === 'console' && (
          <Console
            tsCode={typescript}
            onRun={handleRunStart}
            disabled={!!error || !typescript}
          />
        )}
      </div>
    </div>
  );
}

export default Output;
